/**
 * UpgradePrompt Component - Trade Loop v2.0
 *
 * Contextual upgrade prompts that appear when users hit limits
 * or try to access premium features.
 *
 * USAGE:
 * <UpgradePrompt
 *   reason="limit-reached"
 *   limitType="monthlyReferrals"
 * />
 *
 * <UpgradePrompt
 *   reason="feature-locked"
 *   feature="advancedAnalytics"
 *   variant="banner"
 * />
 */

import React from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { useNavigate } from 'react-router-dom';
import { isPlanPurchasable } from '@/config/subscription-plan-limits';

// ============================================================================
// TYPES
// ============================================================================

type PromptReason = 'limit-reached' | 'limit-warning' | 'feature-locked' | 'general';
type PromptVariant = 'card' | 'banner' | 'inline' | 'modal';
type LimitType = 'monthlyReferrals' | 'totalConnections';

interface UpgradePromptProps {
  /** Reason for showing the prompt */
  reason: PromptReason;
  /** Type of limit (for limit-related prompts) */
  limitType?: LimitType;
  /** Feature name (for feature-locked prompts) */
  feature?: string;
  /** Visual variant */
  variant?: PromptVariant;
  /** Custom title */
  title?: string;
  /** Custom message */
  message?: string;
  /** Show close button (for dismissible prompts) */
  dismissible?: boolean;
  /** Callback when dismissed */
  onDismiss?: () => void;
  /** Custom CTA text */
  ctaText?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function UpgradePrompt({
  reason,
  limitType,
  feature,
  variant = 'card',
  title,
  message,
  dismissible = false,
  onDismiss,
  ctaText
}: UpgradePromptProps) {
  const { planDetails, usage, limits } = useSubscription();
  const navigate = useNavigate();

  // Generate contextual content
  const getContent = () => {
    if (title && message) {
      return { title, message };
    }

    switch (reason) {
      case 'limit-reached':
        if (limitType === 'monthlyReferrals') {
          return {
            title: 'Monthly Referral Limit Reached',
            message: `You've sent all ${limits?.monthlyReferrals} referrals included in your ${planDetails?.name} plan this month. Upgrade to send more!`
          };
        }
        if (limitType === 'totalConnections') {
          return {
            title: 'Connection Limit Reached',
            message: `You've reached your limit of ${limits?.totalConnections} connections on the ${planDetails?.name} plan. Upgrade for unlimited connections!`
          };
        }
        return {
          title: 'Limit Reached',
          message: `You've reached your plan limit. Upgrade to continue.`
        };

      case 'limit-warning':
        if (limitType === 'monthlyReferrals') {
          const percentage = usage && limits ? Math.round((usage.monthlyReferrals / limits.monthlyReferrals) * 100) : 0;
          return {
            title: 'Approaching Referral Limit',
            message: `You've used ${percentage}% of your monthly referrals. Consider upgrading to avoid interruptions.`
          };
        }
        return {
          title: 'Approaching Limit',
          message: 'You\'re close to reaching your plan limit. Upgrade to get more.'
        };

      case 'feature-locked':
        return {
          title: `${feature || 'This Feature'} Requires an Upgrade`,
          message: `${feature || 'This feature'} is available on higher-tier plans. Unlock it by upgrading today!`
        };

      case 'general':
      default:
        return {
          title: 'Unlock Premium Features',
          message: 'Upgrade your plan to access advanced features and higher limits.'
        };
    }
  };

  const content = getContent();
  const buttonText = ctaText || 'View Plans & Upgrade';

  // Determine suggested plan - only suggest a plan the user can actually buy
  let suggestedPlan: string | undefined;
  if (planDetails?.name === 'Starter') {
    suggestedPlan = 'Core';
  } else if (planDetails?.name === 'Core') {
    // Card 24: Grow is visible on the plan screen but not purchasable, and suggesting a plan
    // whose card says "Coming Soon" is a prompt the user cannot act on. Purchasability, not
    // visibility, decides. Otherwise Core users are on the highest available plan.
    if (isPlanPurchasable('grow')) {
      suggestedPlan = 'Grow';
    }
  }

  // Render different variants
  if (variant === 'banner') {
    return (
      <div className="relative bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-4 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className="font-bold">{content.title}</h4>
              <p className="text-sm text-white/90">{content.message}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/plans')}
              className="px-4 py-2 bg-white text-orange-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              {buttonText}
            </button>
            {dismissible && onDismiss && (
              <button
                onClick={onDismiss}
                className="text-white/70 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <span className="text-sm font-medium text-gray-900">{content.title}</span>
        </div>
        <button
          onClick={() => navigate('/plans')}
          className="text-sm font-semibold text-orange-600 hover:text-orange-700"
        >
          Upgrade →
        </button>
      </div>
    );
  }

  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full p-8 relative">
          {dismissible && onDismiss && (
            <button
              onClick={onDismiss}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}

          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>

          <h3 className="text-2xl font-bold text-center mb-2">{content.title}</h3>
          <p className="text-gray-600 text-center mb-6">{content.message}</p>

          <button
            onClick={() => navigate('/plans')}
            className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {buttonText}
          </button>
        </div>
      </div>
    );
  }

  // Default: card variant
  return (
    <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 relative">
      {dismissible && onDismiss && (
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      )}

      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>

        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-900 mb-1">{content.title}</h3>
          <p className="text-gray-600 mb-4">{content.message}</p>

          <div className="flex gap-3">
            <button
              onClick={() => navigate('/plans')}
              className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              {buttonText}
            </button>

            <button
              onClick={() => navigate('/plans')}
              className="px-4 py-2 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Compare Plans
            </button>
          </div>

          {/* Plan suggestion - only show if there's a suggestion */}
          {suggestedPlan && (
            <p className="mt-3 text-sm text-gray-500">
              We recommend the <span className="font-semibold text-orange-600">{suggestedPlan}</span> plan
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
